import React, { useRef } from 'react';
import { Div, DivProps } from '@kizi/core-components';
import {
  ModalWithAccordion,
  ModalWithAccordionProps,
} from './modals/ModalWithAccordion';
import {
  ModalWithCustomContent,
  ModalWithCustomContentProps,
} from './modals/ModalWithCustomContent';
import { useFields } from '@kizi/comparator-core';

export interface PromotionBarItemProps extends DivProps {
  modal: ModalWithAccordionProps | ModalWithCustomContentProps;
  dependsOn?: {
    fieldName: string;
    fieldValue: string | number | boolean;
  };
  itemKey?: number;
  ofTypes?: Array<string>;
  incentive?: {
    krn: string;
    productIncentiveType: string;
    label: string;
    incentiveAmount?: string;
  };
}
export interface PromotionBarProps extends DivProps {
  items: PromotionBarItemProps[];
  productTypes?: Array<string>;
  incentive?: {
    krn: string;
    productIncentiveType: string;
    label: string;
  };
  setShowPromotionalbanner?: Function;
  containerStyles?: any;
  leftSideContent?: any;
}

const PromotionItem: React.FC<PromotionBarItemProps> = ({
  itemKey,
  ...restProps
}) => {
  const marginLeft = itemKey && itemKey > 0 ? 6 : 0;
  return (
    <PromotionBarItemInner
      itemKey={itemKey}
      ml={[0, null, marginLeft]}
      {...restProps}
    />
  );
};

export const PromotionBar: React.FC<PromotionBarProps> = ({
  items,
  productTypes,
  incentive,
  setShowPromotionalbanner,
  leftSideContent,
  ...restProps
}) => {
  const fields = useFields(items.map((i) => i.dependsOn?.fieldName ?? ''));

  const notIncentiveItems = items
    .filter((props: PromotionBarItemProps, i: number) => {
      let shouldShow = true;
      const field = fields[i];
      const { dependsOn, ofTypes } = props;
      if (dependsOn && (!field || field.value !== dependsOn?.fieldValue)) {
        shouldShow = false;
      }
      if (ofTypes && productTypes && shouldShow) {
        shouldShow = productTypes.every((productType) =>
          ofTypes.includes(productType),
        );
      }

      return shouldShow;
    })
    .filter((props: PromotionBarItemProps) => {
      return props?.modal?.contentType !== 'incentive';
    })
    .map((props: PromotionBarItemProps, key) => {
      return (
        <React.Fragment key={key}>
          <PromotionItem {...props} itemKey={key} />
        </React.Fragment>
      );
    });

  const incentiveItems = items
    .filter((props: PromotionBarItemProps) => {
      return props?.modal?.contentType === 'incentive' && Boolean(incentive);
    })
    .map((props: PromotionBarItemProps, key) => {
      return (
        <Div key={key} paddingLeft={[0, 3]}>
          <PromotionItem {...props} incentive={incentive} itemKey={key} />
        </Div>
      );
    });

  setShowPromotionalbanner?.(
    notIncentiveItems.length > 0 ||
      incentiveItems.length > 0 ||
      leftSideContent !== undefined,
  );

  return (
    <Div
      variant="promotionBar"
      display={
        incentiveItems.length > 0 ||
        notIncentiveItems.length > 0 ||
        leftSideContent !== undefined
          ? 'flex'
          : 'none'
      }
      flexWrap="wrap"
      justifyContent={['flex-start', null, 'flex-end']}
      {...restProps}
    >
      {leftSideContent && leftSideContent}
      {[notIncentiveItems, incentiveItems]}
    </Div>
  );
};

export const PromotionBarItemInner: React.FC<PromotionBarItemProps> = ({
  modal,
  incentive,
  ...restProps
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  if (!modal) return null;
  const buttonLabel = incentive?.incentiveAmount
    ? `€${incentive?.incentiveAmount || 'amount missing'} ${incentive?.label || ''}`
    : incentive?.label || '';
  const isIncentive = incentive !== null && incentive !== undefined;
  const modalProps =
    isIncentive &&
    ({
      ...modal,
      content: modal.content ?? [],
      buttonLabel,
    } as ModalWithCustomContentProps);
  return (
    <Div
      key={restProps.itemKey}
      display="flex"
      flex={['0 0 100%', 'unset', null]}
      ref={divRef}
      variant="promotionBarItem"
      {...restProps}
    >
      {isIncentive ? (
        // @ts-ignore
        <ModalWithCustomContent
          ref={divRef}
          variant="promotionItem"
          {...modalProps}
        />
      ) : (
        <ModalWithAccordion ref={divRef} variant="promotionItem" {...modal} />
      )}
    </Div>
  );
};
